import { APP_INITIALIZER, Injector, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from "@angular/common/http";
import { TranslateHttpLoader } from "@ngx-translate/http-loader";
import { TranslateLoader, TranslateModule } from "@ngx-translate/core";
import { API_BASE_URL } from "@shared/clients/api.generated";
import { environment } from "@environments/environment";
import { LandingPageModule } from "./pages/landing-page/landing-page.module";
import { RouteReuseStrategy, RouterModule, TitleStrategy } from "@angular/router";
import { DatePipe, registerLocaleData } from "@angular/common";
import { default as dkLocale } from '@angular/common/locales/da';
import { HeaderModule } from '@shared/components/header/header.module';
import { FooterModule } from '@valg-shared/components/footer/footer.module';
import { TableModule } from '@valg-shared/components/table/table.module';
import { ValgFilterDialogModule } from '@valg-shared/components/table/table/valg-filter-dialog/valg-filter-dialog.module';
import { ValgInputModule } from '@valg-shared/components/input/input.module';
import { ConfirmationDialogModule } from '@valg-shared/components/confirmation-dialog/confirmation-dialog.module';
import { InputDropdownModule } from '@valg-shared/components/input/input-dropdown/input-dropdown.module';
import { CardModule } from '@valg-shared/components/card/card.module';
import { SidenavigationModule } from '@shared/components/sidenavigation/sidenavigation.module';
import { MatIconModule } from '@angular/material/icon';
import { SvgIconService } from '@valg-shared/services/svg-icon.service';
import { ResponsiveMenuModule } from '@shared/components/responsive-menu/responsive-menu.module';
import { BarChartModule } from '@shared/components/bar-chart/bar-chart.module';
import { BarChartRendererModule } from '@shared/components/table/renderers/bar-chart-renderer/bar-chart-renderer.module';
import { ResponsiveBarChartRendererModule } from '@shared/components/table/renderers/responsive-bar-chart-renderer/responsive-bar-chart-renderer.module';
import { BrowserAnimationsModule } from "@angular/platform-browser/animations";
import { FaElectionOverviewModule } from "./pages/election-overview/referendum/fa-election-overview.module";
import { FvElectionOverviewModule } from "./pages/election-overview/general-election/fv-election-overview.module";
import { EvElectionOverviewModule } from "./pages/election-overview/european-parliament-election/ev-election-overview.module";
import { RvElectionOverviewModule } from "./pages/election-overview/regional-election/rv-election-overview.module";
import { KvElectionOverviewModule } from "./pages/election-overview/municipal-election/kv-election-overview.module";
import { DateService } from '@shared/services/date-service.service';
import { BreadcrumbsModule } from '@shared/components/breadcrumbs/breadcrumbs.module';
import { BreadcrumbService } from '@shared/services/breadcrumbs.service';
import { ElectionContextProviderService } from '@shared/services/election-context-provider.service';
import { ElectionContextHttpInterceptor } from "./interceptor/election-context-http-interceptor";
import { ValgDKSearchModule } from '@shared/components/input/valgdk-search/valgdk-search.module';
import { DetailsNominationDistrictModule } from './pages/details-page/nomination-district/nomination-district.module';
import { ColorStatusRendererModule } from '@shared/components/table/renderers/color-status-renderer/color-status-renderer.module';
import { LinkRendererModule } from '@shared/components/table/renderers/link-renderer/link-renderer.module';
import { EnumRendererModule } from '@shared/components/table/renderers/enum-renderer/enum-renderer.module';
import { VotingDetailsModule } from '@shared/components/voting-details/voting-details.module';
import { TopCandidatesModule } from '@shared/components/top-candidates/top-candidates.module';
import { CountStatusModule } from '@shared/components/count-status/count-status.module';
import { ExpandablePartyTableModule } from '@shared/components/expandable-party-list/expandable-party-table.module';
import { DetailsRegionModule } from './pages/details-page/region/region.module';
import { ElectoralCoalitionModule } from '@shared/components/cards/electoral-coalition/electoral-coalition.module';
import { CollectionRendererModule } from '@shared/components/table/renderers/collection-renderer/collection-renderer.module';
import { CoalitionListModule } from '@shared/components/cards/coalition-list/coalition-list.module';
import { MandatesListModule } from '@shared/components/cards/mandates-list/mandates-list.module';
import { CandidateMandatesModule } from '@shared/components/cards/candidate-mandates/candidate-mandates.module';
import { PageLayoutModule } from '@shared/components/page-layout/page-layout.module';
import { DetailsPollingDistrictModule } from './pages/details-page/polling-district/polling-district.module';
import { ReferendumStatusModule } from '@shared/components/cards/referendum-status/referendum-status.module';
import { PrivacyPolicyModule } from './pages/privacy-policy/privacy-policy.module';
import { ExportDataPageModule } from "./pages/export-data-page/export-data-page.module";
import { DetailsMunicipalModule } from './pages/details-page/municipal/municipal.module';
import { SpinnerModule } from "@valg-shared/components/spinner/spinner.module";
import { CardInformationContentModule } from '@shared/components/cards/card-information-content/card-information-content.module';
import { PageDynamicSubheaderModule } from "@valg-shared/components/page-dynamic-subheader/page-dynamic-subheader.module";
import { AppConfigService } from "@valg-shared/services/configuration/app.config.service";
import { UrlConfiguration } from "@valg-shared/components/page-dynamic-subheader/page-dynamic-subheader.component";
import { CustomRouteReuseStrategyService } from '@shared/services/custom-route-reuse-strategy.service';
import {
  PartyVotingStatusSummaryModule
} from "@shared/components/party-voting-status-summary/party-voting-status-summary.module";
import { SiteMapModule } from './pages/site-map/site-map.module';
import { TranslateTitleStrategy } from '@shared/utils/translate-title-strategy';
import { SideNavService } from '@valg-shared/services/sidenav.service'
import { FilterPipe } from "@valg-shared/components/input/input-dropdown/filter.pipe";
import { ResultSummaryModule } from '@shared/components/result-summary/result-summary.module';
import { SimpleResultSummaryModule } from '@shared/components/simple-result-summary/simple-result-summary.module';

export function createTranslateLoader(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

export function initializeAppFactory(
  appConfigService: AppConfigService<UrlConfiguration>
): (() => Promise<void>) {
  return () => appConfigService.loadAppConfig()
}

@NgModule({
  declarations: [
    AppComponent,
  ],
  imports: [
    BrowserModule,
    AppRoutingModule,
    RouterModule,
    HttpClientModule,
    TranslateModule.forRoot({
      defaultLanguage: 'da',
      loader: {
        provide: TranslateLoader,
        useFactory: createTranslateLoader,
        deps: [ HttpClient ],
      },
    }),
    LandingPageModule,
    HeaderModule,
    TableModule,
    ValgFilterDialogModule,
    ValgInputModule,
    InputDropdownModule,
    ConfirmationDialogModule,
    SidenavigationModule,
    ResponsiveMenuModule,
    MatIconModule,
    BarChartModule,
    BarChartRendererModule,
    ResponsiveBarChartRendererModule,
    CollectionRendererModule,
    ColorStatusRendererModule,
    LinkRendererModule,
    EnumRendererModule,
    CardModule,
    BrowserAnimationsModule,
    FaElectionOverviewModule,
    FvElectionOverviewModule,
    EvElectionOverviewModule,
    RvElectionOverviewModule,
    KvElectionOverviewModule,
    BreadcrumbsModule,
    ValgDKSearchModule,
    FooterModule,
    DetailsNominationDistrictModule,
    VotingDetailsModule,
    TopCandidatesModule,
    CountStatusModule,
    ExpandablePartyTableModule,
    DetailsRegionModule,
    ElectoralCoalitionModule,
    CoalitionListModule,
    MandatesListModule,
    CandidateMandatesModule,
    PageLayoutModule,
    DetailsPollingDistrictModule,
    DetailsMunicipalModule,
    ReferendumStatusModule,
    PrivacyPolicyModule,
    ExportDataPageModule,
    SpinnerModule,
    CardInformationContentModule,
    PageDynamicSubheaderModule,
    PartyVotingStatusSummaryModule,
    SiteMapModule,
    ResultSummaryModule,
    SimpleResultSummaryModule
  ],
  providers: [
    DatePipe,
    FilterPipe,
    {
      provide: RouteReuseStrategy,
      useClass: CustomRouteReuseStrategyService
    },
    { provide: API_BASE_URL, useValue: environment.apiRoot },
    {
      provide: APP_INITIALIZER,
      useFactory: initializeAppFactory,
      deps: [
        AppConfigService
      ],
      multi: true
    },
    { provide: LOCALE_ID, useValue: 'da-DK' },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ElectionContextHttpInterceptor,
      multi: true,
    },
    {
      provide: AppConfigService,
      useFactory: (injector: Injector) => (new AppConfigService<UrlConfiguration>(injector)),
      deps: [ Injector ]
    },
    SvgIconService,
    DatePipe,
    DateService,
    BreadcrumbService,
    SideNavService,
    ElectionContextProviderService,
    { provide: TitleStrategy, useClass: TranslateTitleStrategy }
  ],
  bootstrap: [ AppComponent ]
})
export class AppModule {

  constructor() {
    registerLocaleData(dkLocale);
  }
}
